import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, CheckCircle2, Clock, AlertTriangle, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ProductCard, useProducts } from "@/components/ProductCard";
import { Panel, PageHead, Label } from "@/components/common";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const SponsorForm = ({ refresh }) => {
  const api = useApi();
  const [code, setCode] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const s = await api("post", "/me/sponsor", { code }); toast.success(`Sponsor set: ${s.name}`); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="mt-6 rounded-xl border border-[#1F293D] bg-[#0B0F17] p-4" data-testid="sponsor-form">
      <p className="text-sm text-slate-300">Were you referred? Enter your sponsor's code before paying.</p>
      <div className="mt-3 flex gap-2">
        <Input data-testid="sponsor-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ISCXXXXX" className="font-mono" />
        <Button data-testid="sponsor-submit-btn" type="submit" variant="secondary" disabled={!code}>Link</Button>
      </div>
    </form>
  );
};

const PayCard = ({ s, amount, product }) => {
  const uri = `upi://pay?pa=${encodeURIComponent(s.upi_id)}&pn=${encodeURIComponent(s.upi_name)}&am=${amount}&cu=INR&tn=${encodeURIComponent(product ? `Activation - ${product.name}` : "Indian Small Capital activation")}`;
  return (
    <Panel>
      <Label>{product ? "Step 2 · Pay" : "Step 1 · Pay"}</Label>
      <h3 className="mt-2 font-display text-2xl font-bold text-white">Pay <span className="text-emerald-400" data-testid="activation-amount">{inr(amount)}</span></h3>
      {product && <p className="mt-1 text-sm text-slate-400" data-testid="activation-selected-product">for {product.name}</p>}
      <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row">
        <div className="rounded-xl bg-white p-4" data-testid="upi-qr"><QRCodeSVG value={uri} size={168} /></div>
        <div className="space-y-3 text-sm">
          <p className="text-slate-400">Scan with GPay, PhonePe, Paytm or any UPI app, or pay to:</p>
          <button onClick={() => { navigator.clipboard.writeText(s.upi_id); toast.success("UPI ID copied"); }} data-testid="copy-upi-id-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-[#1F293D] bg-[#0B0F17] px-3 py-2 font-mono text-emerald-400 hover:border-emerald-500/50">
            <span data-testid="admin-upi-id">{s.upi_id}</span><Copy className="h-3.5 w-3.5" />
          </button>
          <p className="text-slate-500">Payee: {s.upi_name}</p>
        </div>
      </div>
    </Panel>
  );
};

const UtrForm = ({ refresh, rejected, product }) => {
  const api = useApi();
  const [utr, setUtr] = useState("");
  const [address, setAddress] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size > 5 * 1024 * 1024) { toast.error("Screenshot must be under 5 MB"); e.target.value = ""; return; }
    setFile(f || null);
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const fd = new FormData();
    fd.append("utr", utr);
    if (product) { fd.append("product_id", product.id); fd.append("address", address); }
    if (file) fd.append("screenshot", file);
    try { await api("post", "/me/activation", fd); toast.success("UTR submitted for verification"); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const needsProduct = product === undefined;
  return (
    <Panel>
      <Label>{product !== null ? "Step 3 · Confirm" : "Step 2 · Confirm"}</Label>
      <h3 className="mt-2 font-display text-2xl font-bold text-white">Submit your UTR</h3>
      <p className="mt-2 text-sm text-slate-400">After paying, find the 12-digit UTR / UPI reference number in your UPI app's transaction details.</p>
      {rejected && (
        <div className="mt-4 flex gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300" data-testid="utr-rejected-alert">
          <AlertTriangle className="h-4 w-4 shrink-0" />Previous UTR {rejected.utr} was rejected{rejected.reason ? `: ${rejected.reason}` : ""}.
        </div>
      )}
      <form onSubmit={submit} className="mt-6 space-y-3">
        <Input data-testid="utr-input" inputMode="numeric" maxLength={12} value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 412345678901" className="h-12 font-mono text-lg tracking-widest" />
        {product && <Textarea data-testid="delivery-address-input" placeholder="Full delivery address with PIN code and phone number" value={address} onChange={(e) => setAddress(e.target.value)} />}
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#1F293D] bg-[#0B0F17] px-4 py-3 text-sm text-slate-400 transition-colors hover:border-emerald-500/50" data-testid="screenshot-dropzone">
          {file ? <img src={URL.createObjectURL(file)} alt="Payment screenshot" className="h-12 w-12 rounded object-cover" data-testid="screenshot-preview" /> : <ImagePlus className="h-5 w-5 text-emerald-400" />}
          <span className="min-w-0 flex-1 truncate">{file ? file.name : "Attach payment screenshot (optional, helps faster approval)"}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="screenshot-input" />
        </label>
        <Button data-testid="submit-utr-button" type="submit" disabled={utr.length !== 12 || busy || needsProduct || (product && address.trim().length < 10)} className="h-11 w-full rounded-full font-semibold">{busy ? "Submitting…" : needsProduct ? "Choose a product first" : "Submit for verification"}</Button>
      </form>
    </Panel>
  );
};

const StateCard = ({ icon: Icon, color, title, text, testId }) => (
  <Panel className="flex items-start gap-4" data-testid={testId}>
    <Icon className={`mt-1 h-6 w-6 ${color}`} />
    <div><p className="font-display text-2xl font-bold text-white">{title}</p><p className="mt-1 text-sm text-slate-400">{text}</p></div>
  </Panel>
);

const ProductPicker = ({ products, selected, onSelect }) => (
  <Panel className="mt-6">
    <Label>Step 1 · Choose your product</Label>
    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="activation-products">
      {products.map((p) => <ProductCard key={p.id} p={p} selected={selected?.id === p.id} onSelect={() => onSelect(p)} />)}
    </div>
  </Panel>
);

const InactiveFlow = ({ me, refresh }) => {
  const products = useProducts();
  const [selected, setSelected] = useState(undefined);
  if (!products) return <p className="text-sm text-slate-500">Loading…</p>;
  const hasProducts = products.length > 0;
  const product = hasProducts ? selected : null;
  const amount = product ? product.price : me.settings.package_price;
  return (
    <>
      {hasProducts && <ProductPicker products={products} selected={selected} onSelect={setSelected} />}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {(!hasProducts || product) ? <PayCard s={me.settings} amount={amount} product={product} /> : <Panel className="grid place-items-center text-sm text-slate-500" data-testid="pay-placeholder">Choose a product above to see the payment QR.</Panel>}
        <UtrForm refresh={refresh} rejected={me.last_rejected} product={product} />
      </div>
    </>
  );
};

export default function Activate() {
  const { me, refresh } = useOutletContext();
  const st = me.user.status;
  return (
    <div data-testid="activation-page">
      <PageHead label="Activation" title="Activate your ID" />
      {st === "active" && <StateCard testId="activation-active" icon={CheckCircle2} color="text-emerald-400" title="Your ID is active" text={`Activated on ${fmtDate(me.user.activated_at)}. Share your link to earn ${inr(me.settings.referral_bonus)} per referral.`} />}
      {st === "pending" && <StateCard testId="activation-pending" icon={Clock} color="text-amber-400" title="Awaiting verification" text={`UTR ${me.pending_payment?.utr} submitted on ${fmtDate(me.pending_payment?.created_at)}. You'll be activated once the admin confirms the payment.`} />}
      {st === "inactive" && (
        <>
          {!me.sponsor && <SponsorForm refresh={refresh} />}
          {me.sponsor && <p className="mb-6 text-sm text-slate-400" data-testid="activation-sponsor">Sponsor: <span className="text-white">{me.sponsor.name}</span> <span className="font-mono text-emerald-400">{me.sponsor.referral_code}</span></p>}
          <InactiveFlow me={me} refresh={refresh} />
        </>
      )}
    </div>
  );
}
